import React from 'react';
import { Space } from 'antd';
import { Paragraph } from 'app/components/Typography/Paragraph';
import moment from 'moment';
import { urlify } from 'app/utils';
import classes from './styles.module.scss';

import {
  InspectionPOReportGroup,
  InspectionPOItem
} from '../../../../modules/inspection/types/index';
import { Caption } from 'app/components/Typography/Caption';
import { DateCell } from 'app/components/Table/Cells/DateCell';
import { Link } from 'react-router-dom';

export const DescriptionFields = [
  {
    label: 'Inspection Date',
    name: 'inspectionDate',
    defaultValue: 'N/A',
    render: (data: any) => {
      const { inspectionDate, inspectionEndDate, noOfManday } = data;
      const inspectionDateFormatted = inspectionDate
        ? moment(inspectionDate).format('DD/MM/YYYY')
        : 'N/A';
      const inspectionEndDateFormatted = inspectionEndDate
        ? moment(inspectionEndDate).format('DD/MM/YYYY')
        : 'N/A';
      const mandayFormatted = noOfManday
        ? `(${noOfManday} ${noOfManday > 1 ? 'days' : 'day'})`
        : '';
      return (
        <Paragraph>{`${inspectionDateFormatted} ${
          inspectionEndDateFormatted ? ' - ' + inspectionEndDateFormatted : ''
        } ${mandayFormatted}`}</Paragraph>
      );
    }
  },
  {
    label: 'Inspection Type',
    name: 'typeOfService',
    defaultValue: 'N/A'
  },
  {
    label: 'Date Created',
    name: 'createdAt',
    render: (data) => {
      const { createdAt } = data;
      return (
        <DateCell
          className={classes.dateCell}
          value={createdAt}
          format="DD MMM YYYY"
        />
      );
    }
  },
  {
    label: 'General Inspection Level',
    name: 'generalInspectLevel',
    defaultValue: 'N/A',
    render: (values: any) => {
      const listValues = [values?.generalInspectLevel || 'N/A'];
      const inspectionPOReportGroup: InspectionPOReportGroup =
        values.inspectionPOReportGroup;

      if (inspectionPOReportGroup) {
        const poItems = inspectionPOReportGroup.inspectionPOItems || [];
        const haveCustomized = poItems.some((item: InspectionPOItem) => {
          return item.isCustomSampling;
        });
        if (haveCustomized) {
          listValues.push('(Customized)');
        }
      }
      return listValues.join(' ');
    }
  },
  {
    label: 'Acceptance Quality Level',
    name: '',
    defaultValue: 'N/A',
    render: (values: any) => {
      let haveCustomSampling = false;
      const inspectionPOReportGroup: InspectionPOReportGroup =
        values.inspectionPOReportGroup;
      if (inspectionPOReportGroup) {
        const poItems = inspectionPOReportGroup.inspectionPOItems || [];
        haveCustomSampling = poItems.some((item: InspectionPOItem) => {
          return item.isCustomSampling;
        });
      }
      return (
        <Space wrap>
          <Paragraph>
            Critical{' '}
            {haveCustomSampling ? (
              <Caption style={{ fontSize: 'inherit' }}>/</Caption>
            ) : (
              values?.criticalLevel
            )}
          </Paragraph>
          <Paragraph>
            Major{' '}
            {haveCustomSampling ? (
              <Caption style={{ fontSize: 'inherit' }}>/</Caption>
            ) : (
              values?.criticalMajorLevel
            )}
          </Paragraph>
          <Paragraph>
            Minor{' '}
            {haveCustomSampling ? (
              <Caption style={{ fontSize: 'inherit' }}>/</Caption>
            ) : (
              values?.criticalMinorLevel
            )}
          </Paragraph>
        </Space>
      );
    }
  },
  {
    label: 'Inspector',
    name: 'assignTo',
    defaultValue: 'N/A',
    render: (data: any) => {
      const { assignTo }: any = data;
      return (
        <div>
          {(assignTo || []).map(
            ({ firstName, lastName, inspectionDates }: any, index: number) => {
              const inspectionDateLabel = inspectionDates.map(
                ({ date, manday }: { date: string; manday: number }) => {
                  return `${moment(date).format('DD/MM/YYYY')} (${manday} ${
                    manday > 1 ? 'days' : 'day'
                  })`;
                }
              );
              return (
                <Paragraph key={index}>
                  {`${firstName} ${lastName}`}: {inspectionDateLabel.join(', ')}
                </Paragraph>
              );
            }
          )}
        </div>
      );
    }
  },
  {
    label: 'Rejected Reason By Inspector',
    name: 'rejectReasons',
    defaultValue: 'N/A',
    render: (data: any) => {
      const { assignTo, rejectReasons }: any = data;
      if (!rejectReasons || !rejectReasons.length) return <></>;
      return (
        <div>
          {(assignTo || []).map(
            ({ firstName, lastName, userId }: any, index: number) => {
              const rejectReason = (rejectReasons || []).find((reject: any) => {
                return reject.userId === userId;
              });
              return (
                <Paragraph key={index}>
                  {`${firstName} ${lastName}`}: {rejectReason?.reason || 'N/A'}
                </Paragraph>
              );
            }
          )}
        </div>
      );
    }
  },
  {
    label: 'Check In / Check Out ',
    name: '',
    defaultValue: 'N/A',
    render: (data: any) => {
      const { checkinTime, checkoutTime } = data;
      const checkinTimeFormatted = checkinTime
        ? moment(checkinTime).format('DD/MM/YYYY hh:mm A')
        : 'N/A';
      const checkoutTimeFormatted = checkoutTime
        ? moment(checkoutTime).format('DD/MM/YYYY hh:mm A')
        : 'N/A';

      return (
        <Paragraph>{`${checkinTimeFormatted} / ${checkoutTimeFormatted}`}</Paragraph>
      );
    }
  },
  {
    label: 'Related Job',
    name: 'relatedJob',
    render: (data: {
      relatedJob: {
        inspectionId: string;
        jobNo: string;
      };
    }) => {
      if (!data.relatedJob) return 'N/A';
      return (
        <Link
          to={`/dashboard/inspection/job/${data.relatedJob.inspectionId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {data.relatedJob?.jobNo}
        </Link>
      );
    }
  },
  {
    label: 'Remark',
    name: 'remark',
    multiple: true,
    defaultValue: `N/A`,
    render: (values: any) => {
      return (
        <Paragraph>
          <p
            dangerouslySetInnerHTML={{
              __html: `<div>${urlify(values.remark)}</div>`
            }}
          ></p>
        </Paragraph>
      );
    }
  }
];
